import {
  IconDefinition,
  faFile,
  faFileAudio,
  faFileCsv,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";
import { Nullable } from "../../../../shared/src/@types/util";
import { EUFinanceCaseAttachment } from "../models/sobjects/EUFinanceCaseAttachment";

export const getMimeTypeByExtension = (
  mimeTypeToExtensionsMap: Nullable<Record<string, string[]>>,
  extension: string
): string | undefined => {
  const targetExtension = extension.startsWith(".")
    ? extension
    : `.${extension}`;
  const entry = Object.entries(mimeTypeToExtensionsMap ?? {}).find(
    ([, extensions]) =>
      extensions.some(
        (currentExtension) => currentExtension === targetExtension
      )
  );
  return entry ? entry[0] : undefined;
};

export const getMimeType = (
  mimeTypeToExtensionsMap: Nullable<Record<string, string[]>>,
  file: EUFinanceCaseAttachment
): string | undefined => {
  const extension = (file.fileName ?? file.name)?.split(".")?.reverse()?.[0];
  return getMimeTypeByExtension(mimeTypeToExtensionsMap, extension);
};

export const getFileIcon = (
  mimeTypeToExtensionsMap: Nullable<Record<string, string[]>>,
  file: EUFinanceCaseAttachment
): IconDefinition => {
  const type = file.type ?? getMimeType(mimeTypeToExtensionsMap, file) ?? "";
  if (type === "application/pdf") {
    return faFilePdf;
  }
  if (
    type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    type.startsWith("application/msword")
  ) {
    return faFileWord;
  }
  if (
    type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    type.startsWith("application/vnd.ms-excel")
  ) {
    return faFileExcel;
  }
  if (
    type ===
      "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    type.startsWith("application/vnd.ms-powerpoint")
  ) {
    return faFilePowerpoint;
  }
  if (type.startsWith("audio/")) {
    return faFileAudio;
  }
  if (type.startsWith("image/")) {
    return faFileImage;
  }
  if (type.startsWith("video/")) {
    return faFileVideo;
  }
  if (type === "text/csv") {
    return faFileCsv;
  }
  return faFile;
};

export const BROWSER_VIEWABLE_EXTENSIONS = [
  "txt",
  "html",
  "htm",
  "xml",
  "css",
  "js",
  "jpeg",
  "jpg",
  "png",
  "gif",
  "bmp",
  "svg",
  "webp",
  "mp3",
  "wav",
  "ogg",
  "m4a",
  "mp4",
  "webm",
  "ogg",
  "pdf",
  "json",
  "md",
];

export const openFileInTarget = (
  target: string,
  blob: Blob,
  fileExtension: string | undefined,
  fileName: string | undefined,
  mimeType: string | undefined
): void => {
  const blobWithMimeType = mimeType
    ? new Blob([blob], { type: mimeType })
    : blob;
  const url = window.URL.createObjectURL(blobWithMimeType);
  const link = document.createElement("a");
  link.href = url;
  link.target = target;
  link.rel = "noopener noreferrer";
  if (
    !BROWSER_VIEWABLE_EXTENSIONS.includes(fileExtension as string) &&
    fileName
  ) {
    link.download = fileName;
  }
  link.click();
  // Revoke after triggering download
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export type OpenFileHandler = (
  blob: Blob,
  fileExtension: string | undefined,
  fileName: string | undefined,
  mimeType: string | undefined
) => void;

export const openFileInNewTab: OpenFileHandler = (
  blob,
  fileExtension,
  fileName,
  mimeType
) => {
  openFileInTarget("_blank", blob, fileExtension, fileName, mimeType);
};

export const openFileInCurrentTab: OpenFileHandler = (
  blob,
  fileExtension,
  fileName,
  mimeType
) => {
  openFileInTarget("_self", blob, fileExtension, fileName, mimeType);
};

const EXCEL_FILE_EXTENSIONS = ["xls", "xlsx", "xlsm"];

const WORD_FILE_EXTENSIONS = ["doc", "docx"];

export const isExcelFileByExtension = (
  extension: Nullable<string>
): boolean => {
  return (
    !!extension &&
    (extension.startsWith(".")
      ? EXCEL_FILE_EXTENSIONS.includes(extension.substring(1))
      : EXCEL_FILE_EXTENSIONS.includes(extension))
  );
};

export const isWordFileByExtension = (extension: Nullable<string>): boolean => {
  return (
    !!extension &&
    (extension.startsWith(".")
      ? WORD_FILE_EXTENSIONS.includes(extension.substring(1))
      : WORD_FILE_EXTENSIONS.includes(extension))
  );
};
